'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
const MicPermissionContext = createContext(undefined);
export const MicPermissionProvider = ({ children }) => {
    const [micPermission, setMicPermission] = useState('unknown');
    // Check permission status on mount
    useEffect(() => {
        const checkStatus = async () => {
            if (!navigator.permissions) {
                setMicPermission('prompt');
                return;
            }
            try {
                const result = await navigator.permissions.query({ name: 'microphone' });
                const updateState = async () => {
                    let state = result.state;
                    // Double-check 'granted' status. In some browsers (like Firefox), 
                    // the permissions API might report 'granted' even if the 
                    // user still needs to be prompted for the specific session.
                    if (state === 'granted') {
                        try {
                            const devices = await navigator.mediaDevices.enumerateDevices();
                            const hasLabels = devices.some(device => device.label !== '');
                            if (!hasLabels) {
                                // If we have no labels, we don't actually have active permission
                                state = 'prompt';
                            }
                        }
                        catch (e) {
                            console.error('Error checking device labels:', e);
                        }
                    }
                    setMicPermission(state);
                };
                await updateState();
                result.onchange = updateState;
            }
            catch (error) {
                // Firefox throws error for microphone permission query in older versions
                // or if the configuration is restricted.
                setMicPermission('prompt');
            }
        };
        checkStatus();
    }, []);
    // Explicit request (must be called from user action)
    const requestMicPermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            // Stop immediately
            stream.getTracks().forEach((t) => t.stop());
            setMicPermission('granted');
            return 'granted';
        }
        catch (_a) {
            setMicPermission('denied');
            return 'denied';
        }
    };
    return (_jsx(MicPermissionContext.Provider, { value: { micPermission, requestMicPermission }, children: children }));
};
// Custom hook
export const useMicPermission = () => {
    const context = useContext(MicPermissionContext);
    if (!context) {
        throw new Error('useMicPermission must be used inside MicPermissionProvider');
    }
    return context;
};
